import { useIsClientUser } from '@src/clientUser/useClientUser';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { DocumentFormFileUpload } from '@src/documents/components/DocumentFormFileUpload';
import { DocumentTable } from '@src/documents/components/DocumentTable';
import { FormFileModes } from '@src/documents/components/FormFileModes';
import type { FormDocumentType } from '@src/documents/components/UpsertFormDocument';
import { DocumentAssembler } from '@src/documents/DocumentAssembler';
import { useFindAllDocumentsForOrder } from '@src/documents/hooks/useFindAllDocumentsForOrder';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import { OrderDocumentUploader } from '@src/orders/OrderDocumentUploader';
import { DocumentsFields } from '@src/permits/completePermit/fields/CompletePermitFields';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  orderId: string;
  readonly?: boolean;
} & CollapseListener;

export const Documents = ({ orderId, readonly, ...props }: Props) => {
  const { t } = useTranslation();
  const isClientUser = useIsClientUser();

  const { documents, refetch: refetchDocuments } =
    useFindAllDocumentsForOrder(orderId);

  const { control, reset } = useForm<FormDocumentType>({
    defaultValues: {
      [DocumentsFields.fieldName]: DocumentAssembler.toFormData(documents),
    },
  });

  useCallback(() => {
    reset({
      [DocumentsFields.fieldName]: DocumentAssembler.toFormData(documents),
    });
  }, [documents, reset]);

  return (
    <EditOrderCollapsible
      title={t('documents.label')}
      id="document-section"
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
    >
      <Stack gap={theme.spacing['3']}>
        {isClientUser && (
          <>
            <OrderDocumentUploader
              orderId={orderId}
              onUploadSuccess={refetchDocuments}
            />
            <DocumentTable documents={documents} readonly={readonly} />
          </>
        )}
        {!isClientUser && (
          <DocumentFormFileUpload
            orderId={orderId}
            control={control}
            onSuccessfulUpload={refetchDocuments}
            mode={FormFileModes.MAKE_VISIBLE_TO_CLIENT}
            readOnly={readonly}
          />
        )}
      </Stack>
    </EditOrderCollapsible>
  );
};
